import { useSignOut } from "@gadgetinc/react";
import {
  ArrowRightStartOnRectangleIcon,
  BuildingOfficeIcon,
  ChatBubbleOvalLeftEllipsisIcon,
  ChevronUpIcon,
  ClipboardDocumentIcon,
  Cog8ToothIcon,
  CpuChipIcon,
  CubeIcon,
  IdentificationIcon,
  MegaphoneIcon,
  PresentationChartBarIcon,
  ShieldCheckIcon,
  UserIcon,
} from "@heroicons/react/16/solid";
import posthog from "posthog-js";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import logo from "../assets/logo.png";
import { CustomerContext } from "../providers";
import { LoadScenarioProvider } from "./contexts/LoadScenarioContext";
import { calculateProgress } from "./onboardingPages/calculateProgress";
import ProgressBar from "./ProgressBar";
import { SwitchCustomerDialog } from "./SwitchCustomerDialog";
import { Avatar } from "./tailwind/avatar";
import { Button } from "./tailwind/button";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "./tailwind/dropdown";
import { Link } from "./tailwind/link";
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from "./tailwind/sidebar";
import { SidebarLayout } from "./tailwind/sidebar-layout";
import { Text } from "./tailwind/text";
import { useIsMobile } from "./useIsMobile";

export function LayoutSidebar({ onboardingProgress, children }) {
  const navigate = useNavigate();
  const { user, customer } = useContext(CustomerContext);
  const [progression, setProgression] = useState(0);
  const isMobile = useIsMobile();
  const [isSwitchCustomerDialogOpen, setIsSwitchCustomerDialogOpen] =
    useState(false);

  const getInitials = () => {
    return `${user.firstName?.charAt(0)}${user.lastName?.charAt(0)}`;
  };
  const location = useLocation();
  var selectedMenu = "/" + location.pathname.split("/")[1];

  const signOut = useSignOut({ redirectToPath: "/dashboard" });

  useEffect(() => {
    if (onboardingProgress && onboardingProgress.completed === false) {
      const percentage = calculateProgress(onboardingProgress);
      setProgression(percentage);
    }
  }, [onboardingProgress]);

  return (
    <LoadScenarioProvider>
      <SidebarLayout
        className="max-h-100vh"
        isBgGrey={location.pathname.split("/")[1] === "dashboard"}
        sidebar={
          <Sidebar>
            <SidebarHeader id="header">
              <SidebarLabel
                onClick={() => navigate("/dashboard")}
                className="flex flex-center m-auto my-2"
              >
                <div className="logo m-5">
                  <img src={logo} alt="logo Simio" />
                </div>
              </SidebarLabel>
            </SidebarHeader>
            <SidebarBody>
              {onboardingProgress && !onboardingProgress.completed && (
                <SidebarSection>
                  <Link
                    className="border rounded-customBorderRadius p-3 bg-white shadow-shadowcustom hover:shadow-shadowcustomhover hover:cursor-pointer"
                    onClick={() => navigate("/onboarding")}
                  >
                    <Text className="text-xl text-center mb-2">
                      Setup Guide
                    </Text>
                    <ProgressBar progress={progression} height={2} />

                    <Text className="text-xs mt-1">
                      Finish setting up your workspace and start sending
                      messages.
                    </Text>
                  </Link>
                </SidebarSection>
              )}
              <SidebarSection>
                <SidebarItem
                  href="/conversations/all"
                  current={selectedMenu === "/conversations"}
                >
                  <ChatBubbleOvalLeftEllipsisIcon />
                  <SidebarLabel>Conversations</SidebarLabel>
                </SidebarItem>

                <SidebarItem
                  href="/campaigns"
                  current={selectedMenu === "/campaigns"}
                >
                  <MegaphoneIcon />
                  <SidebarLabel>Campaigns</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                  href="/automations"
                  current={selectedMenu === "/automations"}
                >
                  <CubeIcon />
                  <SidebarLabel>Automations</SidebarLabel>
                </SidebarItem>
                <SidebarItem
                  href="/chatbots"
                  current={selectedMenu === "/chatbots"}
                >
                  <CpuChipIcon />
                  <SidebarLabel>Chatbots</SidebarLabel>
                </SidebarItem>

                <SidebarItem
                  href="/analytics"
                  current={selectedMenu === "/analytics"}
                >
                  <PresentationChartBarIcon />
                  <SidebarLabel>Analytics</SidebarLabel>
                </SidebarItem>

                {!customer.shouldHidePersonalData && (
                  <SidebarItem
                    href="/contacts"
                    current={selectedMenu === "/contacts"}
                  >
                    <IdentificationIcon />
                    <SidebarLabel>Contacts</SidebarLabel>
                  </SidebarItem>
                )}
              </SidebarSection>

              <SidebarSpacer />
            </SidebarBody>
            <SidebarFooter className="">
              <Button
                color="primaryButton"
                className="mb-5"
                onClick={() =>
                  window.open("https://help.getsimio.com/", "_blank")
                }
              >
                Help Center
              </Button>

              <Dropdown>
                <DropdownButton as={SidebarItem}>
                  <span className="flex min-w-0 items-center gap-3">
                    <Avatar
                      className="size-10 bg-pink text-white"
                      square
                      initials={getInitials()}
                      alt=""
                    />
                    <span className="min-w-0">
                      <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">
                        {user.firstName}
                      </span>
                      <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                        {customer.name}
                      </span>
                    </span>
                  </span>
                  <ChevronUpIcon />
                </DropdownButton>
                <DropdownMenu className="min-w-64" anchor="top start">
                  <DropdownItem
                    onClick={() => setIsSwitchCustomerDialogOpen(true)}
                  >
                    <BuildingOfficeIcon />
                    <DropdownLabel>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          Current company
                        </span>
                        <span className="text-xs text-zinc-500">
                          {customer.name}
                        </span>
                      </div>
                    </DropdownLabel>
                  </DropdownItem>
                  <DropdownDivider />

                  {!isMobile && (
                    <>
                      <DropdownItem
                        onClick={() => navigate("/settings/whatsapp/connexion")}
                      >
                        <Cog8ToothIcon />
                        <DropdownLabel>Settings </DropdownLabel>
                      </DropdownItem>
                      <DropdownItem
                        onClick={() => navigate("/settings/company/users")}
                      >
                        <UserIcon />
                        <DropdownLabel>Users</DropdownLabel>
                      </DropdownItem>
                      <DropdownItem onClick={() => navigate("/templates")}>
                        <ClipboardDocumentIcon />
                        <DropdownLabel>WhatsApp templates</DropdownLabel>
                      </DropdownItem>
                    </>
                  )}

                  <DropdownItem
                    onClick={() =>
                      window.open("https://help.getsimio.com/", "_blank")
                    }
                  >
                    <ShieldCheckIcon />
                    <DropdownLabel>Documentation</DropdownLabel>
                  </DropdownItem>
                  <DropdownDivider />

                  <DropdownItem
                    onClick={() => {
                      signOut();
                      posthog.reset();
                    }}
                  >
                    <ArrowRightStartOnRectangleIcon />
                    <DropdownLabel>Sign out</DropdownLabel>
                  </DropdownItem>
                </DropdownMenu>
              </Dropdown>
            </SidebarFooter>
          </Sidebar>
        }
      >
        {children}
      </SidebarLayout>

      <SwitchCustomerDialog
        isOpen={isSwitchCustomerDialogOpen}
        onClose={() => setIsSwitchCustomerDialogOpen(false)}
      />
    </LoadScenarioProvider>
  );
}
